import { useSubscription } from "@apollo/client/react";
import { ON_CHAT_LIST_UPDATE, ON_MESSAGE, type ChatListEvent, type MessageEvent } from "./subscriptions";

interface UseChatLiveUpdatesOptions {
    /** When set, also subscribe to new messages in this chat. */
    chatId?: string;
    /** Called for each new message in `chatId`. */
    onMessage?: (message: MessageEvent) => void;
    /** Called for each change to the caller's chat list. */
    onChatListUpdate?: (event: ChatListEvent) => void;
    /** Subscribe only when true (e.g. once the user is authenticated). */
    enabled?: boolean;
}

/**
 * Subscribes to live chat updates via AppSync.
 *
 * - `onChatListUpdate` fires for every change to the caller's chat list
 *   (new chat, last-message preview, membership) — drives the chat-list UI.
 * - `onMessage` fires for new messages in the currently-open `chatId` — drives
 *   the conversation view.
 *
 * The callbacks let the caller update Apollo cache or local state however suits
 * their UI. Returns the latest event of each kind for convenience.
 */
export function useChatLiveUpdates({
    chatId,
    onMessage,
    onChatListUpdate,
    enabled = true,
}: UseChatLiveUpdatesOptions = {}) {
    const chatList = useSubscription<{ onChatListUpdate: ChatListEvent }>(ON_CHAT_LIST_UPDATE, {
        skip: !enabled,
        onData: ({ data }) => {
            const event = data.data?.onChatListUpdate;
            if (event) onChatListUpdate?.(event);
        },
    });

    const message = useSubscription<{ onMessage: MessageEvent }, { chatId: string }>(ON_MESSAGE, {
        // `skip` prevents this running until a real chatId is present.
        skip: !enabled || !chatId,
        variables: { chatId: chatId ?? "" },
        onData: ({ data }) => {
            const event = data.data?.onMessage;
            if (event) onMessage?.(event);
        },
    });

    return {
        latestChatListEvent: chatList.data?.onChatListUpdate ?? null,
        latestMessage: message.data?.onMessage ?? null,
    };
}
